import { formatearMedioPago } from '../../../utils/gastos'
import {
  diasDesde,
  formatearNumero,
  formatearPorcentaje,
  nombreDiaIso,
} from '../../../utils/analiticaPos'
import {
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
} from '../../../utils/pos'
import {
  GraficoBarrasVerticales,
  GraficoRankingHorizontal,
} from './GraficoBarras'
import MetricCard from './MetricCard'

function Bloque({ titulo, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-base font-bold text-slate-900">{titulo}</h3>
      {children}
    </div>
  )
}

function GrupoCards({ children }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {children}
    </div>
  )
}

function ResumenAnalitica({ datos }) {
  if (!datos) return null

  const { generales: g, comparacion_anterior: cmp } = datos
  const variacion = cmp?.variacion_pct || {}

  if (g.cantidad_ventas === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <p className="text-slate-500">
          No hay ventas POS para los filtros seleccionados.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Comparación destacada vs. período anterior */}
      <GrupoCards>
        <MetricCard
          etiqueta="Total vendido"
          valor={formatearMontoPos(g.total_vendido)}
          variacionPct={variacion.total_vendido}
        />
        <MetricCard
          etiqueta="Kilos vendidos"
          valor={formatearKilosPos(g.kilos_vendidos)}
          variacionPct={variacion.kilos_vendidos}
        />
        <MetricCard
          etiqueta="Clientes con compras"
          valor={formatearNumero(g.clientes_con_compras)}
          variacionPct={variacion.clientes_con_compras}
        />
        <MetricCard
          etiqueta="Precio promedio/kg"
          valor={formatearMontoPos(g.precio_promedio_kg)}
          variacionPct={variacion.precio_promedio_kg}
          tooltip="Suma del monto de las líneas vendidas dividido la suma de kilos netos (promedio ponderado, no promedio simple de $/kg por línea)."
        />
      </GrupoCards>

      <Bloque titulo="Comercial">
        <GrupoCards>
          <MetricCard etiqueta="Ventas" valor={formatearNumero(g.cantidad_ventas)} />
          <MetricCard etiqueta="Subtotal vendido" valor={formatearMontoPos(g.subtotal_vendido)} />
          <MetricCard etiqueta="Ticket promedio" valor={formatearMontoPos(g.ticket_promedio)} tooltip="Total vendido / cantidad de ventas." />
          <MetricCard etiqueta="Ticket mediano" valor={formatearMontoPos(g.ticket_mediano)} />
          <MetricCard etiqueta="Venta promedio/cliente" valor={formatearMontoPos(g.venta_promedio_cliente)} />
          <MetricCard etiqueta="Kilos promedio/venta" valor={formatearKilosPos(g.kilos_promedio_venta)} />
          <MetricCard etiqueta="Kilos promedio/cliente" valor={formatearKilosPos(g.kilos_promedio_cliente)} />
          <MetricCard etiqueta="Ventas anuladas" valor={formatearNumero(g.ventas_anuladas)} subvalor={formatearMontoPos(g.monto_anulado)} />
        </GrupoCards>
      </Bloque>

      <Bloque titulo="Cobranza">
        <GrupoCards>
          <MetricCard etiqueta="Monto pagado" valor={formatearMontoPos(g.monto_pagado)} subvalor={formatearPorcentaje(g.pct_cobrado)} />
          <MetricCard etiqueta="Saldo pendiente" valor={formatearMontoPos(g.saldo_pendiente)} subvalor={formatearPorcentaje(g.pct_pendiente)} />
          <MetricCard etiqueta="Ventas pagadas" valor={formatearNumero(g.ventas_pagadas)} subvalor={formatearPorcentaje(g.pct_ventas_pagadas)} />
          <MetricCard etiqueta="Ventas parciales" valor={formatearNumero(g.ventas_parciales)} subvalor={formatearPorcentaje(g.pct_ventas_parciales)} />
          <MetricCard etiqueta="Ventas pendientes" valor={formatearNumero(g.ventas_pendientes)} subvalor={formatearPorcentaje(g.pct_ventas_pendientes)} />
          <MetricCard etiqueta="Saldo promedio pendiente" valor={formatearMontoPos(datos.cobranza.saldo_promedio_pendiente)} />
          <MetricCard etiqueta="Mayor saldo pendiente" valor={formatearMontoPos(datos.cobranza.mayor_saldo_pendiente)} />
          <MetricCard
            etiqueta="Días prom. saldo pendiente"
            valor={formatearNumero(datos.cobranza.dias_promedio_saldo_pendiente, 1)}
            tooltip="Días transcurridos desde fecha_venta hasta hoy, promediado solo entre ventas con saldo pendiente."
          />
        </GrupoCards>
      </Bloque>

      <Bloque titulo="Descuentos">
        <GrupoCards>
          <MetricCard etiqueta="Total descuentos" valor={formatearMontoPos(datos.descuentos.total_descuentos)} />
          <MetricCard etiqueta="Ventas con descuento" valor={formatearNumero(datos.descuentos.cantidad_ventas_con_descuento)} subvalor={formatearPorcentaje(datos.descuentos.pct_ventas_con_descuento)} />
          <MetricCard etiqueta="Descuento promedio" valor={formatearMontoPos(datos.descuentos.descuento_promedio)} subvalor={formatearPorcentaje(datos.descuentos.descuento_promedio_pct)} />
          <MetricCard etiqueta="Mayor descuento" valor={formatearMontoPos(datos.descuentos.mayor_descuento)} />
          <MetricCard
            etiqueta="Impacto sobre subtotal"
            valor={formatearPorcentaje(datos.descuentos.impacto_pct_sobre_subtotal)}
            tooltip="Total de descuentos otorgados dividido el subtotal vendido del período."
          />
        </GrupoCards>
      </Bloque>

      <Bloque titulo="Clientes">
        <GrupoCards>
          <MetricCard
            etiqueta="Clientes nuevos"
            valor={formatearNumero(g.clientes_nuevos)}
            subvalor={`${formatearMontoPos(g.monto_clientes_nuevos)} (${formatearPorcentaje(g.pct_venta_clientes_nuevos)})`}
            tooltip="Clientes cuya primera venta POS registrada (histórica) cae dentro del período filtrado."
          />
          <MetricCard
            etiqueta="Clientes recurrentes"
            valor={formatearNumero(g.clientes_recurrentes)}
            subvalor={`${formatearMontoPos(g.monto_clientes_recurrentes)} (${formatearPorcentaje(g.pct_venta_clientes_recurrentes)})`}
            tooltip="Clientes con más de una venta histórica registrada."
          />
          {datos.retencion && (
            <MetricCard
              etiqueta="Tasa de recompra"
              valor={formatearPorcentaje(datos.retencion.tasa_recompra_pct)}
              subvalor={`${datos.retencion.clientes_que_volvieron} de ${datos.retencion.clientes_periodo_anterior} volvieron`}
              tooltip="% de clientes del período anterior que volvieron a comprar en el período actual."
            />
          )}
        </GrupoCards>
      </Bloque>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Bloque titulo="Medios de pago">
          <GraficoRankingHorizontal
            datos={datos.medios_pago.map((m) => ({
              etiqueta: formatearMedioPago(m.medio_pago),
              valor: m.monto,
              subetiqueta: `${m.cantidad} pagos · ${formatearPorcentaje(m.pct_del_cobrado)} del cobrado`,
            }))}
            formatearValor={formatearMontoPos}
          />
        </Bloque>

        <Bloque titulo="Distribución de ticket">
          <GraficoRankingHorizontal
            datos={datos.distribucion_ticket.map((r) => ({
              etiqueta: r.rango,
              valor: r.monto,
              subetiqueta: `${r.cantidad} ventas`,
            }))}
            formatearValor={formatearMontoPos}
            color="bg-sky-500"
          />
        </Bloque>
      </div>

      <Bloque titulo="Ventas por día de la semana">
        <GraficoBarrasVerticales
          datos={datos.ventas_por_dia_semana.map((d) => ({
            etiqueta: nombreDiaIso(d.dia_iso).slice(0, 3),
            valor: d.monto,
          }))}
          formatearValor={formatearMontoPos}
        />
      </Bloque>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Bloque titulo="Mejor / peor día">
          <GrupoCards>
            <MetricCard
              etiqueta="Mejor día (monto)"
              valor={formatearMontoPos(datos.mejor_dia.por_monto?.monto)}
              subvalor={formatearFechaPos(datos.mejor_dia.por_monto?.fecha)}
            />
            <MetricCard
              etiqueta="Mejor día (kilos)"
              valor={formatearKilosPos(datos.mejor_dia.por_kilos?.kilos)}
              subvalor={formatearFechaPos(datos.mejor_dia.por_kilos?.fecha)}
            />
            <MetricCard
              etiqueta="Mejor día (ventas)"
              valor={formatearNumero(datos.mejor_dia.por_cantidad_ventas?.cantidad)}
              subvalor={formatearFechaPos(datos.mejor_dia.por_cantidad_ventas?.fecha)}
            />
            <MetricCard
              etiqueta="Peor día (monto)"
              valor={formatearMontoPos(datos.peor_dia.por_monto?.monto)}
              subvalor={formatearFechaPos(datos.peor_dia.por_monto?.fecha)}
            />
          </GrupoCards>
        </Bloque>

        <Bloque titulo="Concentración comercial">
          <GrupoCards>
            <MetricCard etiqueta="Top 5 clientes" valor={formatearPorcentaje(datos.concentracion.top5_clientes_pct)} tooltip="% del total vendido generado por los 5 clientes de mayor monto." />
            <MetricCard etiqueta="Top 10 clientes" valor={formatearPorcentaje(datos.concentracion.top10_clientes_pct)} />
            <MetricCard etiqueta="Top 5 productos (monto)" valor={formatearPorcentaje(datos.concentracion.top5_productos_monto_pct)} />
            <MetricCard etiqueta="Top 5 productos (kilos)" valor={formatearPorcentaje(datos.concentracion.top5_productos_kilos_pct)} />
          </GrupoCards>
        </Bloque>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Bloque titulo="Cajas / Rejillas">
          <GrupoCards>
            <MetricCard etiqueta="Ventas pagadas" valor={formatearNumero(datos.envases.ventas_pagadas)} subvalor={formatearPorcentaje(datos.envases.pct_pagadas)} />
            <MetricCard etiqueta="Ventas pendientes" valor={formatearNumero(datos.envases.ventas_pendientes)} subvalor={formatearPorcentaje(datos.envases.pct_pendientes)} />
            <MetricCard etiqueta="Monto pagado" valor={formatearMontoPos(datos.envases.monto_pagado)} />
          </GrupoCards>
        </Bloque>

        <Bloque titulo="Inactividad de clientes">
          <p className="mb-3 text-xs text-slate-500">
            Calculado sobre el historial completo de compras (no solo el período filtrado).
          </p>
          <GrupoCards>
            <MetricCard etiqueta="Sin comprar &gt; 30 días" valor={formatearNumero(datos.inactividad.mas_30_dias)} />
            <MetricCard etiqueta="Sin comprar &gt; 60 días" valor={formatearNumero(datos.inactividad.mas_60_dias)} />
            <MetricCard etiqueta="Sin comprar &gt; 90 días" valor={formatearNumero(datos.inactividad.mas_90_dias)} />
          </GrupoCards>

          {datos.inactividad.top.length > 0 && (
            <div className="mt-4 max-h-64 overflow-y-auto border-t border-slate-100 pt-3">
              {datos.inactividad.top.map((cliente) => (
                <div key={cliente.cliente_id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                  <span className="truncate text-slate-700">{cliente.razon_social}</span>
                  <span className="shrink-0 text-xs font-semibold text-red-500">
                    {diasDesde(cliente.ultima_compra)} días sin comprar
                  </span>
                </div>
              ))}
            </div>
          )}
        </Bloque>
      </div>
    </div>
  )
}

export default ResumenAnalitica
